<template>
  <div v-if="permission" class="inline">
    <slot />
    <div v-if="showMessage">
      <div class="container">
        <p class="lead text-center">
          {{ t("components.AccessRightWrapper.message") }}
        </p>
      </div>
    </div>
  </div>
  <slot v-else-if="fallbackReady" name="not" />
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import { type PropType, computed } from "vue";
import { useRoute } from "vue-router";
import { authorizationReady, hasRight, hasRightOnAnySite } from "@/functions/authorizationState";
import type { RouterAccessRightMetaType } from "@/functions/routeAuthorization";

const props = defineProps({
  right: {
    type: String,
    required: false,
    default: null,
  },
  rights: {
    type: Array as PropType<string[]>,
    required: false,
    default: null,
  },
  siteId: {
    type: Number,
    required: false,
    default: undefined,
  },
  siteIds: {
    type: Array as PropType<number[]>,
    required: false,
    default: undefined,
  },
  anySite: {
    type: Boolean,
    required: false,
    default: false,
  },
  not: {
    type: Boolean,
    required: false,
    default: false,
  },
  some: {
    type: Boolean,
    required: false,
    default: false,
  },
  showMessage: {
    type: Boolean,
    required: false,
    default: false,
  },
});
const route = useRoute();
const routeSiteId = computed(() => {
  const source =
    (route.meta as RouterAccessRightMetaType).resolvedAuthorizationSiteId ??
    route.params.siteId ??
    route.query.siteId;
  if (typeof source !== "string" && typeof source !== "number") return undefined;
  const siteId = Number(source);
  return Number.isInteger(siteId) && siteId > 0 ? siteId : undefined;
});
const fallbackReady = computed(() => !(props.right || props.rights) || authorizationReady.value);

function canReadRight(right: string): boolean {
  if (props.anySite) return hasRightOnAnySite(right);
  if (props.siteIds !== undefined) {
    return props.siteIds.length > 0 && props.siteIds.every((siteId) => hasRight(right, siteId));
  }
  return hasRight(right, props.siteId ?? routeSiteId.value);
}

const permission = computed(() => {
  // A missing or stale snapshot is not evidence that the user lacks a right.
  // Keep both the positive and inverted branches hidden until it is current.
  if ((props.right || props.rights) && !authorizationReady.value) return false;
  let permitted = true;
  // If rights are given, check them (else return false)
  if (props.right || props.rights) {
    let rights = props.rights?.filter((right) => right.trim() !== "");
    // If only one right is given (and no array provided), create a 1-element array (so we can process it the same way as an array of rights)
    if (props.right && !props.rights) {
      rights = [props.right];
    }
    if (props.some) {
      // Check if the user has some rights (OR)
      permitted = rights.some(canReadRight);
    } else {
      // Check if user has all the requested rights (AND) (default case)
      permitted = rights.every(canReadRight);
    }
  }
  // Check if we should invert the result (and do so if requested)
  return props.not ? !permitted : permitted;
});

const { t } = useTranslation();
</script>

<style scoped>
.inline {
  display: contents;
}
</style>
